import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import Tooltip from "@tippyjs/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { API } from "../../backend";
import authHeader from "../../services/auth-header";
import Helper from "../../Helper";
import { useLocation } from "react-router-dom";
import { getTrans as t } from "../../Localization/translations";



export default function EditManualAccount() {
    const navigate = useNavigate();
    const location = useLocation();
    const id = location.state?.id;


    const [manual, setManual] = useState({
        entry_date: new Date(),
        amount: "",
        description: "",
        debit_account: "",
        credit_account: "",
        reference: "",
        status: "New",
        attachment: null,
    });

    const handleAccountingDate = (value) => {
        setManual({
            ...manual,
            entry_date: value,
        });
    };

    const [allocations, setAllocations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [subcompanyOptions, setSubcompanyOptions] = useState([]);
    const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState([]);

    const [subcompany, setSubcompany] = useState("");
    const [lineOfBusiness, setLineOfBusiness] = useState("");

    const [accountOptions, setAccountOptions] = useState([]);
    const [accountLoading, setAccountLoading] = useState(false);


    /* ---------------- LOAD DATA ---------------- */
    useEffect(() => {
        loadManualAccount();
        fetchSubCompanies();
        fetchLineOfBusiness();
        fetchAllAccounts();
    }, []);

    const loadManualAccount = async () => {
        try {
            const res = await axios.get(API + `manualaccounting/${id}/`, {
                headers: authHeader(),
            });

            const data = res.data;

            setSubcompany(data.subcompany || "");
            setLineOfBusiness(data.line_of_business || "");

            setManual({
                entry_date: data.entry_date ? new Date(data.entry_date) : new Date(),
                amount: data.amount,
                description: data.description || "",
                debit_account: data.debit_account || null,
                credit_account: data.credit_account || null,
                reference: data.reference || "",
                status: data.status,
                attachment: null,
            });

            const loadedAllocations = (data.allocations || []).map(a => ({
                contract: a.contract ? { id: a.contract, contract_number: a.contract_number } : null,
                asset: a.asset ? { id: a.asset, asset_desc: a.asset_desc, asset_number: a.asset_number, } : null,
                customer: a.customer ? { value: a.customer, label: a.customer_name } : null,
                amount: a.amount,
                assetOptions: a.asset ? [{ id: a.asset, asset_desc: a.asset_desc, asset_number: a.asset_number, }]
                    : [], customerOptions: a.customer ? [{ value: a.customer, label: a.customer_name }]
                        : []
            }));


            setAllocations(loadedAllocations.length ? loadedAllocations : [
                { contract: null, asset: null, customer: null, amount: "", assetOptions: [], customerOptions: [] }
            ]);

            setIsLoading(false);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load entry");
            setIsLoading(false);
        }
    };

    // const fetchAllAccounts = async () => {
    //     let url = API + "accounting/accounts/";
    //     let all = [];

    //     while (url) {
    //         const res = await axios.get(url, { headers: authHeader() });
    //         all = [...all, ...(res.data.data || [])];
    //         url = res.data.links?.next;
    //     }

    //     const formatted = all.map(acc => ({
    //         value: acc.id,
    //         label: `${acc.account_number} - ${acc.account_code}`,
    //     }));

    //     setAccountOptions(formatted);
    // };


    const fetchAllAccounts = async () => {
        let url = API + "accounting/accounts/";
        let all = [];


        const baseProtocol = new URL(url).protocol;

        while (url) {
            const res = await axios.get(url, { headers: authHeader() });
            all = [...all, ...(res.data.data || [])];

            let next = res.data.links?.next;

            if (next) {
                try {
                    const nextUrl = new URL(next, url);
                    nextUrl.protocol = baseProtocol;
                    next = nextUrl.toString();
                } catch (err) {
                    console.warn("Invalid next URL:", next, err);
                    next = null;
                }
            }

            url = next;
        }

        const formatted = all
            .filter(acc => acc.manual === "Y")   // ✅ ONLY MANUAL YES
            .map(acc => ({
                value: acc.id,
                label: `${acc.account_number} - ${acc.account_code}`,
            }));

        setAccountOptions(formatted);
    };


    const fetchSubCompanies = async () => {
        const res = await axios.get(API + "company/subcompanies/", { headers: authHeader() });
        const data = res.data.data || res.data;
        if (Array.isArray(data)) setSubcompanyOptions(data);
    };

    const fetchLineOfBusiness = async () => {
        const res = await axios.get(API + "company/lobs/", { headers: authHeader() });
        const data = res.data.data || res.data;
        if (Array.isArray(data)) setLineOfBusinessOptions(data);
    };



    /* ---------------- CONTRACT SEARCH ---------------- */
    const loadContractOptions = async (inputValue) => {
        try {
            const searchTerm = inputValue === "" ? "%" : inputValue;
            const response = await axios.get(API + `contracts/contractsactive/filter/?id=${searchTerm}`, {
                headers: authHeader(),
            });
            const data = response.data.data || response.data.results || response.data;
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error("Error loading contracts:", error);
            return [];
        }
    };

    const fetchAssetsForContract = async (contractId, rowIndex) => {
        if (!contractId) return;

        try {
            const response = await axios.get(API + `contracts/asset-view/${contractId}/`, {
                headers: authHeader(),
            });

            let assetsData = [];
            if (Array.isArray(response.data)) {
                assetsData = response.data;
            } else if (response.data.results && Array.isArray(response.data.results)) {
                assetsData = response.data.results;
            } else if (response.data.data && Array.isArray(response.data.data)) {
                assetsData = response.data.data;
            }

            setAllocations(prevAllocations =>
                prevAllocations.map((row, index) =>
                    index === rowIndex ? { ...row, assetOptions: assetsData } : row
                )
            );

        } catch (error) {
            console.error("Error fetching assets:", error);
        }
    };

    const loadCustomerOptions = async (inputValue) => {
        try {
            const search = inputValue === "" ? "%" : inputValue;

            const res = await axios.get(
                API + `parties/customerparty/?customer_name=${search}`,
                { headers: authHeader() }
            );

            const data = res.data.data || res.data.results || res.data;

            return Array.isArray(data)
                ? data.map(c => ({ label: c.customer_name, value: c.id }))
                : [];
        } catch (error) {
            console.error("Error loading customers:", error);
            return [];
        }
    };


    const handleFinalize = async () => {
        try {
            await axios.post(
                API + `manualaccounting/${id}/finalize/`,
                {},
                { headers: authHeader() }
            );

            toast.success("Entry finalized");
            navigate("/transactions");
        } catch (err) {
            toast.error("Failed to finalize");
        }
    };



    const handleGridChange = (index, field, value) => {
        if (field !== 'contract' && field !== 'asset' && field !== 'customer') {
            setAllocations(prev => prev.map((row, i) =>
                i === index ? { ...row, [field]: value } : row
            ));
            return;
        }

        if (field === 'contract') {
            const selectedContract = value;

            setAllocations(prev => prev.map((row, i) => {
                if (i !== index) return row;

                if (selectedContract) {
                    fetchAssetsForContract(selectedContract.id, index);

                    let customerObj = null;
                    let custOptions = [];

                    if (selectedContract.customer_name) {
                        customerObj = {
                            label: selectedContract.customer_name,
                            value: selectedContract.party || "display-only"
                        };
                        custOptions = [customerObj];
                    }

                    return {
                        ...row,
                        contract: selectedContract,
                        customer: customerObj,        // ✅ auto-fill
                        customerOptions: custOptions, // ✅ options available
                        asset: null,
                        assetOptions: []
                    };
                }

                return {
                    ...row,
                    contract: null,
                    customer: null,
                    customerOptions: [],
                    asset: null,
                    assetOptions: []
                };
            }));
        }


        if (field === 'asset') {
            setAllocations(prev => prev.map((row, i) =>
                i === index ? { ...row, asset: value } : row
            ));
        }

        if (field === 'customer') {
            setAllocations(prev => prev.map((row, i) =>
                i === index ? { ...row, customer: value } : row
            ));
        }
    };


    const addNewRow = () => {
        setAllocations([...allocations, { contract: null, asset: null, customer: null, amount: "", assetOptions: [], customerOptions: [] }]);
    };

    const deleteRow = (index) => {
        setAllocations(prev => prev.filter((_, i) => i !== index));
    };

    const totalAllocated = allocations.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

    /* ---------------- FORM CHANGE ---------------- */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setManual({ ...manual, [name]: value });
    };

    const handleFileChange = (e) => {
        setManual({ ...manual, attachment: e.target.files[0] });
    };

    /* ---------------- SAVE ---------------- */
    const handleSave = async () => {

        // Required field validations
        if (!subcompany) {
            toast.error("Company is required");
            return;
        }

        if (!manual.entry_date) {
            toast.error("Accounting Date is required");
            return;
        }

        if (!manual.amount || parseFloat(manual.amount) <= 0) {
            toast.error("Valid Amount is required");
            return;
        }

        if (!manual.debit_account) {
            toast.error("Debit Account is required");
            return;
        }

        if (!manual.credit_account) {
            toast.error("Credit Account is required");
            return;
        }

        if (manual.debit_account === manual.credit_account) {
            toast.error("Debit and Credit account cannot be same");
            return;
        }

        const amount = parseFloat(manual.amount || 0);

        // ✅ remove empty allocation rows
        const validAllocations = allocations.filter(
            row => row.amount && parseFloat(row.amount) > 0
        );

        const totalAllocated = validAllocations.reduce(
            (sum, r) => sum + (parseFloat(r.amount) || 0),
            0
        );

        // ✅ only block if allocations exist AND total > amount
        if (validAllocations.length > 0 && totalAllocated > amount) {
            toast.error("Allocation total cannot be greater than Amount");
            return;
        }

        const payloadAllocations = validAllocations.map(row => ({
            contract: row.contract?.id || null,
            asset: row.asset?.id || null,
            customer: row.customer?.value || null,
            amount: row.amount
        }));

        const payload = {
            ...manual,
            entry_date: Helper.DatePicker(manual.entry_date),
            subcompany: subcompany || null,
            line_of_business: lineOfBusiness || null,
            allocations: payloadAllocations // can be []
        };

        try {
            await axios.put(API + `manualaccounting/${id}/`, payload, {
                headers: authHeader(),
            });

            toast.success("Manual Accounting Entry Updated");
            setTimeout(() => navigate("/transactions"), 1500);
        } catch (err) {
            console.error(err.response?.data);
            toast.error("Update failed");
        }
    };


    return (
        <div className="page-wrapper">
            <ToastContainer />

            <div className="content container-fluid">
                <div className="header pb-0 mb-3 pt-3">
                    <div>
                        <h5>{t("Manual Accounting")}</h5>
                        <h6>
                            <Link to="/manualaccounting" className="tc">
                                {t("Manual Accounting")}
                            </Link>{" "}
                            / <span> {t("Edit Accounting")} </span>
                        </h6>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="success"></div>

                                <form>
                                    <div className="row">

                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label required">
                                                    {t("Company ")}
                                                    <Tooltip content={t("Subcompany_Tooltip")}>
                                                        <i className="fa fa-light fa-circle-info"></i>
                                                    </Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <select className="form-control" value={subcompany} onChange={e => setSubcompany(e.target.value)}>
                                                        <option value="">Select Company</option>
                                                        {subcompanyOptions.map(sc => (
                                                            <option key={sc.id} value={sc.id}>{sc.sub_company_name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="col-form-label">
                                                {t("Line of Business ")}
                                                <Tooltip content={t("Lineofbusiness_Tooltip")}>
                                                    <i className="fa fa-light fa-circle-info"></i>
                                                </Tooltip>
                                            </label>
                                            <select className="form-control" value={lineOfBusiness} onChange={e => setLineOfBusiness(e.target.value)}>
                                                <option value="">Select Line of Business</option>
                                                {lineOfBusinessOptions.map(lob => (
                                                    <option key={lob.id} value={lob.id}>{lob.lob_name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label required">
                                                    {t("Accounting Date")}
                                                    <Tooltip content={t("Date of Manual Accounting")}><i className="fa fa-light fa-circle-info ms-1"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <DatePicker
                                                        className="form-control datepick"
                                                        selected={manual.entry_date}
                                                        onChange={(e) => handleAccountingDate(e)}
                                                        name="entry_date"
                                                        placeholderText={"mm/dd/yyyy"}
                                                        showYearDropdown
                                                        scrollableYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                    />

                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label required">
                                                    {t("Amount")}
                                                    <Tooltip content={t("Amount_Tooltip")}><i className="fa fa-light fa-circle-info ms-1"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <input type="number" name="amount" className="form-control" value={manual.amount} onChange={handleChange} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label required">
                                                    {t("Debit Account")}
                                                    <Tooltip content={t("Amount_Tooltip")}><i className="fa fa-light fa-circle-info ms-1"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <Select
                                                        value={accountOptions.find(a => a.value === manual.debit_account) || null}
                                                        options={accountOptions}
                                                        onChange={(selected) =>
                                                            setManual({ ...manual, debit_account: selected?.value || null })
                                                        }
                                                        placeholder={accountLoading ? "Loading..." : "Select Debit Account"}
                                                        isClearable
                                                    />

                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label required">
                                                    {t("Credit Account")}
                                                    <Tooltip content={t("Amount_Tooltip")}><i className="fa fa-light fa-circle-info ms-1"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <Select
                                                        value={accountOptions.find(a => a.value === manual.credit_account) || null}
                                                        options={accountOptions}
                                                        onChange={(selected) =>
                                                            setManual({ ...manual, credit_account: selected?.value || null })
                                                        }
                                                        placeholder={accountLoading ? "Loading..." : "Select Credit Account"}
                                                        isClearable
                                                    />

                                                </div>
                                            </div>
                                        </div>


                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label">
                                                    {t("Reference")}{" "}
                                                    <Tooltip content={t("Reference")}><i className="fa fa-light fa-circle-info"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <input type="text" name="reference" className="form-control" value={manual.reference} onChange={handleChange} />
                                                </div>
                                            </div>
                                        </div>


                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label">
                                                    {t("Description")}{" "}
                                                    <Tooltip content={t("Description_Tooltip")}><i className="fa fa-light fa-circle-info"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <textarea name="description" className="form-control" rows="3" value={manual.description} onChange={handleChange}></textarea>
                                                </div>
                                            </div>
                                        </div>

                                        {/* <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label">
                                                    {t("Attachments")}{" "}
                                                    <Tooltip content={t("Attachments")}><i className="fa fa-light fa-circle-info"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <input type="file" className="form-control" onChange={handleFileChange} />
                                                </div>
                                            </div>
                                        </div> */}


                                    </div>
                                </form>
                                <div></div>

                                {/* <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'hidden' }}>

                                    <div className="row pt-3">
                                        <div className="col-xl-6"><label className="col-form-label">{t("Items")}</label></div>
                                        <div className="col-xl-6">
                                            <div className="text-right">
                                                <button type="button" className="btn btn-outline-success" onClick={addNewRow}>+ {t("ADD")}</button>
                                            </div>
                                        </div>
                                    </div>

                                    <table className="table table-nowrap mb-0">
                                        <thead style={{ color: 'black' }}>
                                            <tr>
                                                <th className="col-form-label" style={{ minWidth: "200px", color: 'black' }}>
                                                    {t("Contract")}
                                                    <Tooltip content={t("Contract Number")}>
                                                        <i className="fa fa-light fa-circle-info ms-1"></i>
                                                    </Tooltip>
                                                </th>
                                                <th className="col-form-label" style={{ minWidth: "200px", color: 'black' }}>
                                                    {t("Asset")}
                                                    <Tooltip content={t("Asset Name")}>
                                                        <i className="fa fa-light fa-circle-info ms-1"></i>
                                                    </Tooltip>
                                                </th>
                                                <th className="col-form-label" style={{ minWidth: "200px", color: 'black' }}>
                                                    {t("Customer")}
                                                    <Tooltip content={t("Customer Name")}>
                                                        <i className="fa fa-light fa-circle-info ms-1"></i>
                                                    </Tooltip>
                                                </th>
                                                <th className="col-form-label" style={{ minWidth: "150px", color: 'black' }}>
                                                    {t("Amount")}
                                                    <Tooltip content={t("Allocated Amount")}>
                                                        <i className="fa fa-light fa-circle-info ms-1"></i>
                                                    </Tooltip>
                                                </th>

                                                <th className="col-form-label" style={{ width: "50px" }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {allocations.map((row, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <AsyncSelect
                                                            cacheOptions
                                                            defaultOptions
                                                            value={row.contract}
                                                            loadOptions={loadContractOptions}
                                                            onChange={(val) => handleGridChange(index, "contract", val)}
                                                            getOptionLabel={(o) => o.contract_number}
                                                            getOptionValue={(o) => o.id}
                                                            isClearable
                                                        />
                                                    </td>
                                                    <td>
                                                        <Select
                                                            value={row.asset}
                                                            options={row.assetOptions}
                                                            onChange={(val) => handleGridChange(index, "asset", val)}
                                                            isDisabled={!row.contract}
                                                            getOptionLabel={(o) => o.asset_desc || o.asset_number}
                                                            getOptionValue={(o) => o.id}
                                                            isClearable
                                                        />
                                                    </td>
                                                    <td>
                                                        <Select
                                                            value={row.customer}
                                                            options={row.customerOptions}
                                                            onChange={(val) => handleGridChange(index, "customer", val)}
                                                            isDisabled={!row.contract}
                                                            isClearable
                                                        />
                                                    </td>
                                                    <td>
                                                        <input type="number" className="form-control" value={row.amount} onChange={(e) => handleGridChange(index, "amount", e.target.value)} />
                                                    </td>
                                                    <td>
                                                        <button className="btn btn-outline-danger" onClick={() => deleteRow(index)}>x</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="3"></td>
                                                <td className="fw-bold text-end">{totalAllocated.toFixed(2)}</td>
                                            </tr>
                                        </tfoot>
                                    </table>

                                    <div className="text-left py-3">
                                        <button type="button" className="btn btn-secondary" onClick={() => navigate("/transactions")}>CANCEL</button>
                                        &nbsp;
                                        <button className="btn btn-primary" onClick={handleSave}>Update</button>
                                        &nbsp;
                                        <button className="btn btn-primary" onClick={handleFinalize}>Finalize</button>
                                    </div>
                                </div> */}
                                <div className="d-flex align-items-start py-3">
                                    <button type="button" className="btn btn-secondary" onClick={() => navigate("/transactions")}>CANCEL</button>
                                    &nbsp;
                                    <button className="btn btn-primary" onClick={handleSave}>Update</button>
                                    {/* &nbsp;
                                    <button className="btn btn-primary" onClick={handleFinalize}>Finalize</button> */}
                                    <div style={{ marginLeft: "15px", fontSize: "13px", color: "#6c757d" }}>
                                        This transaction needs to be submitted for approval.
                                        <br />
                                        Please navigate to the view screen to submit for approval.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
